import {
  Button,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  useToast,
} from '@chakra-ui/react';
import { yupResolver } from '@hookform/resolvers/yup';
import { ButtonSpinner } from 'components/LoadingSpinner';
import { manageErrorResponse } from 'helpers/manageErrorResponse';
import useUpdateWorkshopLegalFormMutation from 'hooks/mutations/useUpdateWorkshopLegalFormMutation';
import useWorkshopQuery from 'hooks/useWorkshopQuery';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';

type SetLegalFormModalProps = {
  workshopId: number;
  accountId: number;
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
};

const schema = yup
  .object({
    legal_form: yup.string().required(),
  })
  .required();

const SetLegalFormModal = ({ workshopId, accountId, isOpen, onClose, onSuccess }: SetLegalFormModalProps) => {
  const { t } = useTranslation();
  const toast = useToast();
  const workshopQuery = useWorkshopQuery(workshopId);
  const updateLegalFormMutation = useUpdateWorkshopLegalFormMutation(accountId);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isValid },
  } = useForm<{ legal_form: string }>({
    resolver: yupResolver(schema),
    defaultValues: { legal_form: workshopQuery.data?.legal_form ?? '' },
    mode: 'onChange',
  });

  useEffect(() => {
    if (isOpen) {
      reset({ legal_form: workshopQuery.data?.legal_form ?? '' });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, workshopQuery.data]);

  const onSubmit = (data: { legal_form: string }) => {
    if (!workshopQuery.data) {
      return;
    }
    updateLegalFormMutation.mutate(
      { workshop: workshopQuery.data, legalForm: data.legal_form },
      {
        onSuccess: () => onSuccess(),
        onError: (error: any) => manageErrorResponse(toast, error.response),
      },
    );
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <ModalOverlay />
      <ModalContent as="form" onSubmit={handleSubmit(onSubmit)} data-test-id="set-legal-form-modal">
        <ModalHeader>{t('account:status.registration_completed.legal_form_modal.header')}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Text mb={4}>{t('account:status.registration_completed.legal_form_modal.description')}</Text>
          <FormControl isInvalid={!!errors.legal_form}>
            <FormLabel>{t('workshop:legal_form.label')}</FormLabel>
            <Input {...register('legal_form')} type="text" autoComplete="off" />
            <FormErrorMessage>{errors.legal_form?.message}</FormErrorMessage>
          </FormControl>
        </ModalBody>
        <ModalFooter>
          <Button variant="secondary" mr={3} onClick={onClose} disabled={updateLegalFormMutation.isPending}>
            {t('common:cancel')}
          </Button>
          <Button
            type="submit"
            variant="primary"
            disabled={!isValid || workshopQuery.isLoading || updateLegalFormMutation.isPending}
            data-test-id="set-legal-form-submit"
          >
            {t('common:submit')} {updateLegalFormMutation.isPending ? <ButtonSpinner /> : null}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default SetLegalFormModal;
